import { useState, useEffect, useCallback } from 'react';
import { supabase, supabaseAdmin } from '../utils/supabase';

/**
 * Hook personalizado para manejar cache y optimizar consultas a Supabase
 * @param {string} table - Nombre de la tabla
 * @param {object} options - Opciones de configuración
 * @returns {object} - Estado y funciones
 */
export const useSupabaseCache = (table, options = {}) => {
  const {
    orderBy = 'created_at',
    ascending = false,
    limit = 50,
    cacheTime = 5 * 60 * 1000, // 5 minutos por defecto
    select = '*'
  } = options;

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const cacheKey = `${table}_cache`;
  const lastFetchKey = `${table}_last_fetch`;

  const fetchData = useCallback(async (forceRefresh = false) => {
    setLoading(true);
    setError(null);

    try {
      // Verificar cache si no es refresh forzado
      if (!forceRefresh) {
        const lastFetch = localStorage.getItem(lastFetchKey);
        const now = Date.now();
        
        if (lastFetch && (now - parseInt(lastFetch)) < cacheTime) {
          const cachedData = localStorage.getItem(cacheKey);
          if (cachedData) {
            const parsedData = JSON.parse(cachedData);
            setData(parsedData);
            setLoading(false);
            return parsedData;
          }
        }
      }

      // Realizar consulta a Supabase
      let query = supabase
        .from(table)
        .select(select);

      if (orderBy) {
        query = query.order(orderBy, { ascending });
      }

      if (limit) {
        query = query.limit(limit);
      }

      const { data: result, error: queryError } = await query;

      if (queryError) {
        console.error(`Error fetching ${table}:`, queryError);
        setError(`Error al cargar ${table}: ${queryError.message}`);
        setData([]);
        return [];
      }

      const resultData = result || [];
      setData(resultData);

      // Guardar en cache
      localStorage.setItem(cacheKey, JSON.stringify(resultData));
      localStorage.setItem(lastFetchKey, Date.now().toString());

      return resultData;

    } catch (error) {
      console.error(`Network error fetching ${table}:`, error);
      setError('Error de conexión. Verifica tu internet.');
      setData([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, [table, orderBy, ascending, limit, cacheTime, select, cacheKey, lastFetchKey]);

  const clearCache = useCallback(() => {
    localStorage.removeItem(cacheKey);
    localStorage.removeItem(lastFetchKey);
  }, [cacheKey, lastFetchKey]);

  const addItem = useCallback((newItem) => {
    setData(prevData => [newItem, ...prevData]);
    // Actualizar cache
    const updatedData = [newItem, ...data];
    localStorage.setItem(cacheKey, JSON.stringify(updatedData));
  }, [data, cacheKey]);

  const updateItem = useCallback((id, updatedItem) => {
    setData(prevData => 
      prevData.map(item => item.id === id ? updatedItem : item)
    );
    // Actualizar cache
    const updatedData = data.map(item => item.id === id ? updatedItem : item);
    localStorage.setItem(cacheKey, JSON.stringify(updatedData));
  }, [data, cacheKey]);

  const removeItem = useCallback((id) => {
    setData(prevData => prevData.filter(item => item.id !== id));
    // Actualizar cache
    const updatedData = data.filter(item => item.id !== id);
    localStorage.setItem(cacheKey, JSON.stringify(updatedData));
  }, [data, cacheKey]);

  // Cargar datos iniciales
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    loading,
    error,
    fetchData,
    clearCache,
    addItem,
    updateItem,
    removeItem,
    refresh: () => fetchData(true)
  };
};

export default useSupabaseCache;
